import { Injectable } from '@angular/core';
import { FileDownloadInfo, ReportHistory } from '@dpr/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  FileUpload,
  OfflineSyncResponse,
  ProjectSetup,
  Subproject,
  UserSession,
} from '@shared/models';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import { Observable, of } from 'rxjs';
import { mergeMap, take, tap } from 'rxjs/operators';
import {
  BasePersistenceService,
  LocalPersistenceService,
  RemotePersistenceService,
} from '.';
import { DprDetailsSync } from '../../models/dpr';

@UntilDestroy()
@Injectable()
export class PersistenceService {
  action: BasePersistenceService;

  constructor(
    private appFacade: AppFacade,
    private userDetailsFacade: UserDetailsFacade,
    private remotePersistenceService: RemotePersistenceService,
    private localPersistenceService: LocalPersistenceService
  ) {
    this.init();
  }

  reloadUserSession(updateCache: boolean): Observable<UserSession> {
    return this.appFacade.query.isAppOnline$.pipe(
      take(1),
      mergeMap((isAppOnline: boolean) => {
        return isAppOnline
          ? this.remotePersistenceService.getUserSession()
          : of(null);
      }),
      mergeMap((data: UserSession) => {
        if (data && updateCache) {
          this.userDetailsFacade.updateUserSession(data);
        }
        return of(data);
      })
    );
  }

  reloadProjectSetup(
    subprojectId: number,
    updateCache: boolean
  ): Observable<ProjectSetup> {
    return this.appFacade.query.isAppOnline$.pipe(
      take(1),
      mergeMap((isAppOnline: boolean) => {
        return subprojectId && subprojectId > 0 && isAppOnline
          ? this.remotePersistenceService.getProjectSetup(subprojectId)
          : of(null);
      }),
      mergeMap((data: ProjectSetup) => {
        if (updateCache) {
          this.userDetailsFacade.setProjectSetup(data);
        }
        return of(data);
      })
    );
  }

  updateSelectedClient(clientId: string): Observable<UserSession> {
    return this.remotePersistenceService.updateSelectedClient(clientId);
  }

  updateSelectedSubproject(subprojectId: number): Observable<void> {
    return this.remotePersistenceService.updateSelectedSubproject(subprojectId);
  }

  // files

  uploadFile(formData: FormData, uploadConfig: any): Observable<any> {
    return this.remotePersistenceService.uploadFile(formData, uploadConfig);
  }

  downloadFile(externalUrl: string, fileName: string): Observable<any> {
    return this.remotePersistenceService.downloadFile(externalUrl, fileName);
  }

  downloadZipArchive(fileInfo: FileDownloadInfo[]): Observable<string> {
    return this.remotePersistenceService.downloadZipFile(fileInfo);
  }

  getDprReportHistory(dprId: number | string): Observable<ReportHistory> {
    return this.remotePersistenceService.getDprReportHistory(dprId);
  }

  getUserManualDetails(clientId: string): Observable<FileUpload> {
    return this.remotePersistenceService.getUserManualDetails(clientId);
  }

  syncOfflineData(
    syncOfflineGuid: string,
    dprDetailsOfflineChanges: DprDetailsSync[],
    changesSubproject: Subproject | undefined
  ): Observable<OfflineSyncResponse[]> {
    return this.remotePersistenceService.syncOfflineData(
      syncOfflineGuid,
      dprDetailsOfflineChanges,
      changesSubproject
    );
  }

  private init(): void {
    this.appFacade.query.isAppOnline$
      .pipe(
        untilDestroyed(this),
        tap((isAppOnline) => {
          this.action = isAppOnline
            ? this.remotePersistenceService
            : this.localPersistenceService;
        })
      )
      .subscribe();
  }
}
